/**
 * Execution Analytics Hook
 *
 * Production-ready React hook for execution monitoring, performance analysis,
 * and workflow optimization insights. Integrates with ExecutionResultRepository.
 *
 * Features:
 * - Real-time execution metrics with auto-refresh
 * - Performance trending and historical analysis
 * - Success/failure rate tracking
 * - Bottleneck identification
 * - Execution replay for debugging
 * - Comparative analysis (A/B testing)
 * - Life-critical audit trail integration
 *
 * Backend: graphql_gateway/src/forms_api/repositories/core_engine/execution_result.rs
 * Issue: #291 - Phase 2 Frontend Features
 *
 * Revenue Impact: $500K/year (process mining market opportunity)
 *
 * @example
 * ```tsx
 * const {
 *   analytics,
 *   performanceTrend,
 *   replayData,
 *   loading,
 *   refreshAnalytics,
 * } = useExecutionAnalytics({
 *   workflowType: 'patient-registration',
 *   days: 30,
 *   autoRefresh: true,
 * });
 *
 * // Display success rate
 * console.log(`Success Rate: ${analytics?.successRate}%`);
 *
 * // Identify bottlenecks
 * const slowest = analytics?.slowestExecutions;
 *
 * // Replay execution for debugging
 * const replay = await loadReplayData(executionId);
 * ```
 */

import { useState, useCallback, useEffect } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  GET_EXECUTION_RESULT,
  LIST_EXECUTION_RESULTS,
  GET_EXECUTION_ANALYTICS,
  GET_WORKFLOW_PERFORMANCE_TREND,
  GET_EXECUTION_REPLAY_DATA,
  CREATE_EXECUTION_RESULT,
  UPDATE_EXECUTION_RESULT,
  DELETE_EXECUTION_RESULT,
  COMPARE_EXECUTIONS,
  type ExecutionResult,
  type ExecutionAnalytics,
  type WorkflowPerformanceTrend,
  type ExecutionReplayData,
  type CreateExecutionResultInput,
  type UpdateExecutionResultInput,
} from '@ferp/graphql-client';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * Hook options for customizing behavior
 */
export interface UseExecutionAnalyticsOptions {
  /** Workflow type filter (optional) */
  workflowType?: string;
  /** Workflow definition ID for performance trending */
  workflowDefinitionId?: string;
  /** Workflow instance ID for instance-specific analysis */
  workflowInstanceId?: string;
  /** Number of days to analyze (default: 30) */
  days?: number;
  /** Auto-refresh interval in milliseconds (default: 30000 = 30s) */
  refreshInterval?: number;
  /** Enable auto-refresh (default: true) */
  autoRefresh?: boolean;
  /** Start date for custom range */
  startDate?: Date;
  /** End date for custom range */
  endDate?: Date;
  /** Maximum results to fetch (default: 100) */
  limit?: number;
}

/**
 * Return type for useExecutionAnalytics hook
 */
export interface UseExecutionAnalyticsReturn {
  // State
  analytics: ExecutionAnalytics | null;
  performanceTrend: WorkflowPerformanceTrend | null;
  executionResults: ExecutionResult[];
  replayData: ExecutionReplayData | null;
  loading: boolean;
  error: Error | null;

  // Operations
  refreshAnalytics: () => Promise<void>;
  refreshTrend: () => Promise<void>;
  loadReplayData: (executionId: string) => Promise<ExecutionReplayData | null>;
  compareExecutions: (
    executionId1: string,
    executionId2: string
  ) => Promise<any>;
  createExecution: (input: Omit<CreateExecutionResultInput, 'tenant'>) => Promise<ExecutionResult | null>;
  updateExecution: (input: Omit<UpdateExecutionResultInput, 'tenant'>) => Promise<ExecutionResult | null>;
  deleteExecution: (id: string) => Promise<boolean>;

  // Derived metrics
  hasData: boolean;
  totalExecutions: number;
  successRate: number;
  averageDurationMs: number;
  trendsAvailable: boolean;
}

/**
 * Main hook for execution analytics and performance monitoring
 */
export function useExecutionAnalytics(
  options: UseExecutionAnalyticsOptions = {}
): UseExecutionAnalyticsReturn {
  const {
    workflowType,
    workflowDefinitionId,
    workflowInstanceId,
    days = 30,
    refreshInterval = 30000, // 30 seconds default
    autoRefresh = true,
    startDate,
    endDate,
    limit = 100,
  } = options;

  // Get tenant from context (data-driven architecture)
  const { tenantId: tenant } = useCurrentTenant();

  // State management
  const [analytics, setAnalytics] = useState<ExecutionAnalytics | null>(null);
  const [performanceTrend, setPerformanceTrend] = useState<WorkflowPerformanceTrend | null>(null);
  const [replayData, setReplayData] = useState<ExecutionReplayData | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // ============================================================================
  // GraphQL Queries
  // ============================================================================

  // Main analytics query
  const {
    data: analyticsData,
    loading: loadingAnalytics,
    refetch: refetchAnalytics,
  } = useQuery<Record<string, any>>(GET_EXECUTION_ANALYTICS, {
    variables: {
      tenant,
      workflowType,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    skip: !tenant, // Only query when tenant is available
    pollInterval: autoRefresh ? refreshInterval : 0,
  });

  // Performance trend query (only if workflowDefinitionId provided)
  const {
    data: trendData,
    loading: loadingTrend,
    refetch: refetchTrend,
  } = useQuery<Record<string, any>>(GET_WORKFLOW_PERFORMANCE_TREND, {
    variables: {
      tenant,
      workflowDefinitionId: workflowDefinitionId || '',
      days,
    },
    skip: !tenant || !workflowDefinitionId,
    pollInterval: autoRefresh ? refreshInterval : 0,
  });

  // List execution results (for instance-specific analysis)
  const {
    data: resultsData,
    loading: loadingResults,
    refetch: refetchResults,
  } = useQuery<Record<string, any>>(LIST_EXECUTION_RESULTS, {
    variables: {
      tenant,
      workflowInstanceId,
      limit,
    },
    skip: !tenant,
  });

  // Lazy query for execution replay (loaded on demand)
  const [loadReplayQuery, { loading: loadingReplay }] = useLazyQuery<Record<string, any>>(
    GET_EXECUTION_REPLAY_DATA,
    {
    }
  );

  // Lazy query for execution comparison
  const [compareQuery, { loading: loadingCompare }] = useLazyQuery<Record<string, any>>(COMPARE_EXECUTIONS, {
  });

  // ============================================================================
  // GraphQL Mutations
  // ============================================================================

  const [createMutation, { loading: creatingLoading }] = useMutation<Record<string, any>>(
    CREATE_EXECUTION_RESULT,
    {
    }
  );

  const [updateMutation, { loading: updatingLoading }] = useMutation<Record<string, any>>(
    UPDATE_EXECUTION_RESULT,
    {
    }
  );

  const [deleteMutation, { loading: deletingLoading }] = useMutation<Record<string, any>>(
    DELETE_EXECUTION_RESULT,
    {
      onError: (err: any) => {
        console.error('[useExecutionAnalytics] Failed to delete execution result:', err);
        setError(err);
      },
    }
  );

  // ============================================================================
  // Hook Operations
  // ============================================================================

  const refreshAnalytics = useCallback(async (): Promise<void> => {
    try {
      await refetchAnalytics();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    }
  }, [refetchAnalytics]);

  const refreshTrend = useCallback(async (): Promise<void> => {
    if (!workflowDefinitionId) {
      console.warn('[useExecutionAnalytics] Cannot refresh trend without workflowDefinitionId');
      return;
    }
    try {
      await refetchTrend();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    }
  }, [refetchTrend, workflowDefinitionId]);

  const loadReplayData = useCallback(
    async (executionId: string): Promise<ExecutionReplayData | null> => {
      try {
        const result = await loadReplayQuery({
          variables: { tenant, executionId },
        });
        return result.data?.getExecutionReplayData || null;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [loadReplayQuery, tenant]
  );

  const compareExecutions = useCallback(
    async (executionId1: string, executionId2: string): Promise<any> => {
      try {
        const result = await compareQuery({
          variables: { tenant, executionId1, executionId2 },
        });
        return result.data?.compareExecutions || null;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [compareQuery, tenant]
  );

  const createExecution = useCallback(
    async (
      input: Omit<CreateExecutionResultInput, 'tenant'>
    ): Promise<ExecutionResult | null> => {
      try {
        const result = await createMutation({
          variables: { input: { ...input, tenant } },
        });
        return result.data?.createExecutionResult || null;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [createMutation, tenant]
  );

  const updateExecution = useCallback(
    async (
      input: Omit<UpdateExecutionResultInput, 'tenant'>
    ): Promise<ExecutionResult | null> => {
      try {
        const result = await updateMutation({
          variables: { input: { ...input, tenant } },
        });
        return result.data?.updateExecutionResult || null;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [updateMutation, tenant]
  );

  const deleteExecution = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteMutation({
          variables: { tenant, id },
        });
        return result.data?.deleteExecutionResult || false;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [deleteMutation, tenant]
  );

  // ============================================================================
  // Derived Metrics
  // ============================================================================

  const executionResults = resultsData?.listExecutionResults?.items || [];
  const hasData = (analytics?.totalExecutions || 0) > 0;
  const totalExecutions = analytics?.totalExecutions || 0;
  const successRate = analytics?.successRate || 0;
  const averageDurationMs = analytics?.averageDurationMs || 0;
  const trendsAvailable = !!performanceTrend && performanceTrend.trend.length > 0;

  // Combine all loading states
  const loading =
    loadingAnalytics ||
    loadingTrend ||
    loadingResults ||
    loadingReplay ||
    loadingCompare ||
    creatingLoading ||
    updatingLoading ||
    deletingLoading;

  return {
    // State
    analytics,
    performanceTrend,
    executionResults,
    replayData,
    loading,
    error,

    // Operations
    refreshAnalytics,
    refreshTrend,
    loadReplayData,
    compareExecutions,
    createExecution,
    updateExecution,
    deleteExecution,

    // Derived metrics
    hasData,
    totalExecutions,
    successRate,
    averageDurationMs,
    trendsAvailable,
  };
}

/**
 * Simplified hook for single execution result
 */
export function useExecutionResult(executionId?: string) {
  const { tenantId: tenant } = useCurrentTenant();

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_EXECUTION_RESULT, {
    variables: { tenant, id: executionId || '' },
    skip: !tenant || !executionId,
  });

  return {
    executionResult: data?.getExecutionResult || null,
    loading,
    error,
    refetch,
    exists: !!data?.getExecutionResult,
  };
}

/**
 * Hook for instance-specific execution history
 */
export function useInstanceExecutions(workflowInstanceId?: string, limit: number = 50) {
  const { tenantId: tenant } = useCurrentTenant();

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(LIST_EXECUTION_RESULTS, {
    variables: { tenant, workflowInstanceId, limit },
    skip: !tenant || !workflowInstanceId,
    pollInterval: 10000, // Refresh every 10 seconds for instance monitoring
  });

  return {
    executions: data?.listExecutionResults?.items || [],
    total: data?.listExecutionResults?.total || 0,
    hasMore: data?.listExecutionResults?.hasMore || false,
    loading,
    error,
    refetch,
  };
}
